import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import Logo from "../../assets/images/logo/hazer-logo.png";
import { Toaster } from "react-hot-toast";
import apiUrls from "../../config/api-urls";
import { apiRequest } from "../../services/api-client";

export default function VerifyOtpComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pre-fill phone number if redirected from registration
  const initialPhoneNumber = location.state?.phoneNumber || "";
  
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber) {
      setError("Please enter your phone number");
      return;
    }
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setError("OTP must be exactly 6 digits");
      return;
    }
    
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("POST", apiUrls.auth.verifyOtp, {
        phoneNumber,
        otp,
      });
      
      // If successful, redirect to login
      if (response?.data?.success) {
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err) {
      const backendError =
        err.response?.data?.error?.message || 
        err.response?.data?.message || 
        err.message || 
        "Failed to verify OTP. Please check the code and try again.";
      setError(backendError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden relative">
          {/* Close Button */}
          <button
            onClick={() => navigate("/")}
            className="absolute top-4 right-4 text-black p-2 rounded-full hover:bg-[#ff9906] hover:text-white transition z-10"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="flex flex-col lg:flex-row min-h-[600px]">
            {/* Left Panel */}
            <div className="hidden sm:flex sm:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Welcome to Hazer Globals
              </h2>
              <p className="text-sm md:text-lg">
                Verify your account to access our platform features.
              </p>
              <div className="bg-white rounded-2xl mt-4">
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="Building"
                  className="mt-6 w-32 h-32 md:w-40 md:h-40 max-w-xs rounded-lg shadow-lg"
                />
              </div>
            </div>

            {/* Right Panel - OTP Form */}
            <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Verify OTP
                  </h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="phoneNumber"
                      className="text-sm font-medium text-gray-700"
                    >
                      Phone Number
                    </Label>
                    <Input
                      id="phoneNumber"
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder="Enter your phone number"
                      required
                      className="w-full px-4 py-3 bg-gray-100 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#ff8c00] focus:ring-opacity-50"
                    />
                  </div>

                  {/* OTP Code */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="otp"
                      className="text-sm font-medium text-gray-700"
                    >
                      One-Time Password (OTP)
                    </Label>
                    <Input
                      id="otp"
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder="Enter 6-digit OTP code"
                      required
                      className="w-full px-4 py-3 bg-gray-100 border-0 rounded-lg text-center tracking-widest text-lg font-bold focus:bg-white focus:ring-2 focus:ring-[#ff8c00] focus:ring-opacity-50"
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <p className="text-sm text-gray-600">
                    Please enter the 6-digit verification code sent to your phone number. In development, use <span className="font-mono font-bold">000000</span> to bypass.
                  </p>

                  <Button
                    type="submit"
                    disabled={isLoading || !phoneNumber || !otp}
                    className="w-full bg-[#ff8c00] hover:bg-[#e67c00] text-white py-3 rounded-lg font-medium disabled:opacity-50"
                  >
                    {isLoading ? "Verifying..." : "Verify OTP"}
                  </Button>

                  <div className="flex justify-center gap-4">
                    <Link to="/login">
                      <Button
                        variant="outline"
                        type="button"
                        className="px-6 py-2 rounded-lg bg-transparent"
                      >
                        Back to Login
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
